import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCustomers } from '@hooks/useAdminResources';
import { useCustomerBookings } from '@hooks/useBookings';
import { useAuth } from '@hooks/useAuth';
import { useActiveCenter, ALL_CENTERS } from '@hooks/useActiveCenter';
import { adminCustomersApi, ApiRequestError } from '@api/client';
import type { Booking, Customer, CustomerActivity } from '@api/types';
import * as Icons from '@components/Icons';
import { PageHead } from '@components/admin/PageHead';
import { RowMenu } from '@components/admin/RowMenu';
import { BookingDetailDrawer, StatusPill, fmtTime } from './BookingDetailDrawer';
import { MergeCustomerModal } from './CustomersPage.MergeModal';

const MONTHS_ES = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];

function fmtDate(iso: string) {
  if (!iso) return '—';
  const [, m, d] = iso.split('-').map(Number);
  return `${String(d)} ${MONTHS_ES[m - 1] ?? ''} ${iso.slice(0, 4)}`;
}

function fmtDateShort(iso: string | null | undefined) {
  if (!iso) return '—';
  const [, m, d] = iso.slice(0, 10).split('-').map(Number);
  return `${String(d)} ${MONTHS_ES[m - 1] ?? ''}`;
}

function initials(name: string) {
  return name.split(' ').map((p) => p.charAt(0)).join('').slice(0, 2).toUpperCase();
}

function tone(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return Math.abs(h) % 5;
}

// ─── Aside ──────────────────────────────────────────────────────────────────

function HistoryRow({
  booking,
  onOpen,
}: {
  booking: Booking;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="history-row"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        width: '100%',
        padding: '8px 10px',
        background: 'transparent',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {booking.serviceName}
        </div>
        <div className="mono" style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
          {fmtDateShort(booking.startTime)} · {fmtTime(booking.startTime)}
        </div>
      </div>
      <StatusPill status={booking.status} />
    </button>
  );
}

function CustomerDetail({
  customer,
  onEdit,
  onOpenBooking,
}: {
  customer: Customer;
  onEdit: () => void;
  onOpenBooking: (b: Booking) => void;
}) {
  const { data: historyPage, isLoading: historyLoading } = useCustomerBookings(customer.id, 10);
  const history = historyPage?.data ?? [];

  return (
    <aside className="split-detail">
      <div className="detail-head">
        <div className="avatar avatar-lg" data-tone={tone(customer.name)} style={{ width: 44, height: 44, fontSize: 16 }}>
          {initials(customer.name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.015em', margin: '0 0 2px' }}>{customer.name}</h2>
          {customer.centerName && (
            <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{customer.centerName}</div>
          )}
        </div>
        <button className="icon-btn" data-variant="ghost" onClick={onEdit}><Icons.Edit size={14} /></button>
      </div>

      <div className="detail-body">
        <div className="kv-list">
          <div className="kv-row"><span className="kv-k">Email</span><span className="kv-v">{customer.email}</span></div>
          <div className="kv-row"><span className="kv-k">Teléfono</span><span className="kv-v mono">{customer.phone || '—'}</span></div>
          <div className="kv-row">
            <span className="kv-k">Reservas totales</span>
            <span className="kv-v" style={{ fontWeight: 600 }}>{customer.bookingsCount ?? 0}</span>
          </div>
          <div className="kv-row">
            <span className="kv-k">Última visita</span>
            <span className="kv-v mono">{customer.lastVisitAt ? fmtDate(customer.lastVisitAt.slice(0, 10)) : '—'}</span>
          </div>
          <div className="kv-row">
            <span className="kv-k">Cliente desde</span>
            <span className="kv-v mono">{fmtDate(customer.createdAt.slice(0, 10))}</span>
          </div>
        </div>

        <div>
          <div style={{ fontSize: 10.5, color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Historial reciente
          </div>
          {historyLoading ? (
            <div style={{ fontSize: 12.5, color: 'var(--fg-soft)' }}>Cargando…</div>
          ) : history.length === 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--fg-soft)', fontStyle: 'italic' }}>
              Sin reservas todavía.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {history.map((b) => (
                <HistoryRow key={b.id} booking={b} onOpen={() => { onOpenBooking(b); }} />
              ))}
            </div>
          )}
        </div>

        <div>
          <div style={{ fontSize: 10.5, color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Nota interna
          </div>
          {customer.internalNote && customer.internalNote.length > 0 ? (
            <div
              style={{
                padding: '10px 12px',
                background: 'var(--bg-soft)',
                borderLeft: '3px solid var(--accent)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12.5,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
              }}
            >
              {customer.internalNote}
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: 'var(--fg-soft)', fontStyle: 'italic' }}>
              Sin nota. Pulsa el lápiz para añadir una.
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          <a href={`mailto:${customer.email}`} className="btn" style={{ flex: 1 }}>
            <Icons.Mail size={13} /> Enviar email
          </a>
          <Link to="/admin/calendar" className="btn" data-variant="primary" style={{ flex: 1 }}>
            <Icons.Plus size={13} /> Nueva reserva
          </Link>
        </div>
      </div>
    </aside>
  );
}

// ─── Edit modal (unchanged) ─────────────────────────────────────────────────

function CustomerModal({ customer, onClose }: { customer: Customer | null; onClose: () => void }) {
  const qc = useQueryClient();
  const isCreate = customer === null;

  // Create mode: editable PII fields. Edit mode: PII stays read-only (only
  // internalNote is editable from here — full edits live in the merge flow
  // or a future dedicated editor).
  const [name, setName]         = useState(customer?.name ?? '');
  const [email, setEmail]       = useState(customer?.email ?? '');
  const [phone, setPhone]       = useState(customer?.phone ?? '');
  const [internalNote, setInternalNote] = useState(customer?.internalNote ?? '');
  const [formError, setFormError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () => adminCustomersApi.create({
      name:  name.trim(),
      email: email.trim(),
      phone: phone.trim(),
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
      onClose();
    },
    onError: (e: unknown) => {
      // Surface backend validation errors (e.g. invalid email/phone) inline
      // rather than the generic banner so the operator knows what to fix.
      if (e instanceof ApiRequestError && e.status === 422) {
        setFormError('Revisa los campos: email/teléfono no válidos o nombre vacío.');
      } else {
        setFormError(null);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (note: string | null) =>
      adminCustomersApi.update(customer?.id ?? '', { internalNote: note }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
      onClose();
    },
  });

  const pending = createMutation.isPending || updateMutation.isPending;
  const hasError = createMutation.isError || updateMutation.isError;

  function handleSave() {
    if (isCreate) {
      // Quick client-side checks — backend re-validates with Zod regardless,
      // these just avoid the round-trip for obvious mistakes.
      if (name.trim().length === 0 || email.trim().length < 3 || phone.trim().length < 5) {
        setFormError('Nombre, email y teléfono son obligatorios.');
        return;
      }
      setFormError(null);
      createMutation.mutate();
      return;
    }
    const trimmed = internalNote.trim();
    updateMutation.mutate(trimmed.length > 0 ? trimmed : null);
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => { e.stopPropagation(); }}>
        <div className="modal-head">
          <div className="modal-title">{customer ? `Cliente · ${customer.name}` : 'Nuevo cliente'}</div>
          <button className="icon-btn" data-variant="ghost" onClick={onClose}><Icons.X size={14} /></button>
        </div>
        <div className="modal-body">
          <div className="field">
            <label className="field-label">Nombre</label>
            <input
              className="input"
              value={name}
              onChange={(e) => { setName(e.target.value); }}
              readOnly={!isCreate}
              autoFocus={isCreate}
              placeholder={isCreate ? 'Nombre y apellidos' : undefined}
            />
          </div>
          <div className="field-row">
            <div className="field">
              <label className="field-label">Email</label>
              <input
                className="input"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); }}
                readOnly={!isCreate}
                placeholder={isCreate ? 'cliente@ejemplo.com' : undefined}
              />
            </div>
            <div className="field">
              <label className="field-label">Teléfono</label>
              <input
                className="input mono"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); }}
                readOnly={!isCreate}
                placeholder={isCreate ? '+34600000000' : undefined}
              />
            </div>
          </div>
          <div className="field">
            <label className="field-label">Nota interna</label>
            <textarea
              className="textarea"
              value={internalNote}
              onChange={(e) => { setInternalNote(e.target.value); }}
              placeholder={
                isCreate
                  ? 'Disponible tras guardar el cliente.'
                  : 'Alergias, preferencias, contexto operativo… Solo visible para el equipo.'
              }
              rows={4}
              style={{ resize: 'vertical', fontSize: 13 }}
              disabled={isCreate}
            />
            <p style={{ fontSize: 11.5, color: 'var(--fg-soft)', marginTop: 4 }}>
              {isCreate
                ? 'Podrás añadir notas internas tras crear el cliente.'
                : 'Solo el equipo del centro la ve. Aparece también al abrir cualquier reserva de este cliente.'}
            </p>
          </div>
          {formError && (
            <div style={{ fontSize: 12, color: 'var(--danger)' }}>{formError}</div>
          )}
          {hasError && !formError && (
            <div style={{ fontSize: 12, color: 'var(--danger)' }}>
              No se pudo guardar. Inténtalo de nuevo.
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={pending}>Cancelar</button>
          <button
            className="btn"
            data-variant="primary"
            onClick={handleSave}
            disabled={pending}
          >
            {pending ? 'Guardando…' : isCreate ? 'Crear cliente' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

const ACTIVITY_LABEL: Record<CustomerActivity, string> = {
  all: 'Cualquier actividad',
  recent: 'Recientes (≤30 días)',
  inactive: 'Inactivos (>90 días)',
  never: 'Sin reservas',
};

export function CustomersPage() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';

  const [q, setQ] = useState('');
  const [activity, setActivity] = useState<CustomerActivity>('all');
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [modalCustomer, setModalCustomer] = useState<Customer | null | undefined>(undefined);
  const [mergeTarget, setMergeTarget] = useState<Customer | null>(null);
  const [openBooking, setOpenBooking] = useState<Booking | null>(null);

  // Centre filter shared across admin pages — see `useActiveCenter`. The
  // value also flows to the backend via the `X-Center-Id` header, so this
  // `centerId` query param is redundant for non-superadmin but harmless.
  const { activeCenterId, setActiveCenterId, availableCenters, canSwitch, supportsAll } = useActiveCenter();

  const listParams = useMemo<Record<string, string>>(() => {
    const params: Record<string, string> = {
      page: String(page + 1),
      perPage: '20',
    };
    if (q.trim().length > 0) params.q = q.trim();
    if (activity !== 'all') params.activity = activity;
    if (isSuperadmin && activeCenterId !== ALL_CENTERS) params.centerId = activeCenterId;
    return params;
  }, [page, q, activity, activeCenterId, isSuperadmin]);

  const { data, isLoading } = useCustomers(listParams);
  const customers = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const selected = customers.find((c) => c.id === selectedId) ?? customers.at(0) ?? null;

  return (
    <div>
      <PageHead
        title="Clientes"
        sub={`${String(total)} clientes registrados`}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="mono" style={{ fontSize: 12, color: 'var(--fg-soft)' }}>{total} total</span>
            <button
              className="btn"
              data-variant="primary"
              onClick={() => { setModalCustomer(null); }}
            >
              <Icons.Plus size={14} /> Nuevo cliente
            </button>
          </div>
        }
      />

      <div className="toolbar">
        <div className="search">
          <Icons.Search size={14} className="ico" />
          <input
            className="input"
            placeholder="Buscar por nombre o email…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
          />
        </div>
        {canSwitch && (
          <select
            className="input"
            value={activeCenterId}
            onChange={(e) => { setActiveCenterId(e.target.value); setPage(0); }}
            style={{ width: 200 }}
          >
            {supportsAll && <option value={ALL_CENTERS}>Todos los centros</option>}
            {availableCenters.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}
        <select
          className="input"
          value={activity}
          onChange={(e) => { setActivity(e.target.value as CustomerActivity); setPage(0); }}
          style={{ width: 200 }}
        >
          {(Object.keys(ACTIVITY_LABEL) as CustomerActivity[]).map((k) => (
            <option key={k} value={k}>{ACTIVITY_LABEL[k]}</option>
          ))}
        </select>
        <div className="toolbar-spacer" />
        <span className="mono" style={{ fontSize: 11.5, color: 'var(--fg-soft)' }}>
          {customers.length} resultados
        </span>
      </div>

      <div className="split">
        <div className="split-list">
          <div className="card">
            <table className="table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Contacto</th>
                  <th>Centro</th>
                  <th style={{ textAlign: 'right' }}>Reservas</th>
                  <th>Última visita</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>Cargando…</td></tr>
                )}
                {!isLoading && customers.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>Sin clientes</td></tr>
                )}
                {customers.map((c) => (
                  <tr
                    key={c.id}
                    data-clickable="true"
                    data-selected={selected?.id === c.id}
                    onClick={() => { setSelectedId(c.id); }}
                  >
                    <td>
                      <div className="user-cell">
                        <div className="avatar" data-tone={tone(c.name)}>{initials(c.name)}</div>
                        <div className="user-cell-info">
                          <span className="user-cell-name">{c.name}</span>
                          <span className="user-cell-meta mono">{c.id.slice(0, 12)}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: 13 }}>{c.email}</div>
                      {c.phone && <div className="cell-muted mono">{c.phone}</div>}
                    </td>
                    <td style={{ fontSize: 13 }}>{c.centerName ?? '—'}</td>
                    <td className="mono" style={{ textAlign: 'right', fontWeight: 600 }}>
                      {c.bookingsCount ?? 0}
                    </td>
                    <td className="mono cell-muted">{fmtDateShort(c.lastVisitAt)}</td>
                    <td className="cell-narrow">
                      <RowMenu
                        items={[
                          { icon: Icons.Edit, label: 'Ver detalle', onClick: () => { setModalCustomer(c); } },
                          { icon: Icons.Mail, label: 'Enviar email', onClick: () => window.open(`mailto:${c.email}`) },
                          { icon: Icons.Users, label: 'Fusionar con otro cliente…', onClick: () => { setMergeTarget(c); } },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="card-foot">
                <span>Página {page + 1} de {totalPages}</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="icon-btn" disabled={page === 0} onClick={() => { setPage((p) => Math.max(0, p - 1)); }}>
                    <Icons.ChevL size={14} />
                  </button>
                  <button className="icon-btn" disabled={page >= totalPages - 1} onClick={() => { setPage((p) => Math.min(totalPages - 1, p + 1)); }}>
                    <Icons.ChevR size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {selected && (
          <CustomerDetail
            customer={selected}
            onEdit={() => { setModalCustomer(selected); }}
            onOpenBooking={(b) => { setOpenBooking(b); }}
          />
        )}
      </div>

      {modalCustomer !== undefined && (
        <CustomerModal customer={modalCustomer ?? null} onClose={() => { setModalCustomer(undefined); }} />
      )}
      {mergeTarget && (
        <MergeCustomerModal target={mergeTarget} onClose={() => { setMergeTarget(null); }} />
      )}
      {openBooking && (
        <BookingDetailDrawer booking={openBooking} onClose={() => { setOpenBooking(null); }} />
      )}
    </div>
  );
}
